import type { StoreApi } from 'zustand/vanilla'
import { snippetNodeId } from '@/features/source-snippets/object-set'
import type { InvestigationsState } from '@/stores/investigations-store'
import { useObjectSetContributionsStore } from '@/stores/object-set-contributions-store'
import type { GraphView } from '@/types/graph-view'

interface BaseOptions {
  store: StoreApi<InvestigationsState>
  view: GraphView
  label: string
}

export interface ApplyResult {
  investigationKey: string | null
  contributionId: string | null
}

/**
 * "Add to investigation" — additive merge into the active viewpoint (saved
 * investigation OR existing transient). Registers a contribution chip so the
 * user can toggle visibility or remove the contribution cleanly.
 */
export function addContributionToActive(opts: BaseOptions): ApplyResult {
  const { store, view, label } = opts
  const merge = store.getState().addToObjectSet(view, label, 'agent')
  if (merge.addedNodeIds.length === 0 && merge.addedEdgeIds.length === 0) {
    return { investigationKey: null, contributionId: null }
  }
  const investigationKey =
    store.getState().activeId ?? (store.getState().transient ? 'transient' : null)
  if (!investigationKey) return { investigationKey: null, contributionId: null }

  useObjectSetContributionsStore.getState().add({
    id: merge.contributionId,
    investigationKey,
    label,
    source: 'agent',
    nodeIds: merge.addedNodeIds,
    edgeIds: merge.addedEdgeIds,
  })

  return { investigationKey, contributionId: merge.contributionId }
}

/**
 * "Show on graph" — exit the current investigation and open the result as a
 * fresh transient exploration. Lets the user inspect the new result in
 * isolation without polluting (or being polluted by) the prior viewpoint.
 * The prior saved investigation is one click away in the sidebar.
 */
export function openAsExploration(opts: BaseOptions): ApplyResult {
  const { store, view, label } = opts
  // Clear active first — `setActive(null)` preserves whatever transient is
  // there, so we then explicitly install the new one.
  store.getState().setActive(null)
  store.getState().setTransient(view, label)
  // Replace any chips from the prior transient — this is a *fresh*
  // exploration, not an addition. Without this, prior chips bleed into the
  // new view and the chip bar grows on every "Show separately" click.
  const contribStore = useObjectSetContributionsStore.getState()
  contribStore.clear('transient')
  // Register the new exploration as a single chip so users can toggle /
  // remove / save it like any other contribution.
  const contributionId = `c-${Date.now()}`
  contribStore.add({
    id: contributionId,
    investigationKey: 'transient',
    label,
    source: 'agent',
    nodeIds: [...view.nodes.map(n => n.id), ...(view.snippets ?? []).map(s => snippetNodeId(s.id))],
    edgeIds: view.edges.map(e => e.id),
  })
  return { investigationKey: 'transient', contributionId }
}
